import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import '../styles/Auth.css';

const Register = () => {
  const [step, setStep] = useState('details'); // 'details' or 'otp'
  const [otp, setOtp] = useState(new Array(6).fill(''));

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Registrera - TrafikBevakning';
    document.body.style.backgroundColor = '#f5f5f7';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/konto');
    }
  }, [isAuthenticated, navigate]);

  const handleDetailsSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (!name || !email || !password || !confirmPassword) {
      setError('Fyll i alla fält');
      return;
    }

    // Password requirements validation
    const hasLength = password.length >= 8;
    const hasAlpha = /[a-zA-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSymbol = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

    if (!hasLength || !hasAlpha || !hasNumber || !hasSymbol) {
      setError('Lösenordet uppfyller inte kraven');
      return;
    }

    if (password !== confirmPassword) {
      setError('Lösenorden matchar inte');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/register-init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setStep('otp');
        setIsLoading(false);
      } else {
        setError(data.error || 'Kunde inte initiera registrering');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Registration init error:', error);
      setError('Kunde inte ansluta till servern');
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const otpString = otp.join('');

    try {
      const response = await fetch(`${API_BASE_URL}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: otpString }),
      });

      const data = await response.json();

      if (response.ok) {
        // Redirect to login with success message and pre-filled email
        navigate('/login', {
          state: {
            email: email,
            message: 'Registrering och verifiering lyckades! Logga in för att fortsätta.',
          },
        });
      } else {
        setError(data.error || 'Verifiering misslyckades');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setError('Kunde inte ansluta till servern');
      setIsLoading(false);
    }
  };

  const handleChange = (element, index) => {
    if (isNaN(element.value)) return false;

    setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);

    // Focus next input
    if (element.value !== '') {
      if (index < 5) {
        document.getElementById(`otp-input-${index + 1}`).focus();
      }
    }
  };

  const handleBackspace = (e, index) => {
    if (e.key === 'Backspace') {
      if (index > 0 && otp[index] === '') {
        document.getElementById(`otp-input-${index - 1}`).focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const data = e.clipboardData.getData('text').trim();
    if (!/^\d+$/.test(data)) return;

    const newOtp = [...otp];
    for (let i = 0; i < Math.min(data.length, 6); i++) {
      newOtp[i] = data[i];
    }
    setOtp(newOtp);

    const nextIndex = Math.min(data.length, 5);
    document.getElementById(`otp-input-${nextIndex}`)?.focus();
  };

  const passwordRequirements = [
    { label: 'Minst 12 tecken', valid: password.length >= 12 },
    { label: 'Minst 1 bokstav', valid: /[a-zA-Z]/.test(password) },
    { label: 'Minst 1 siffra', valid: /[0-9]/.test(password) },
    { label: 'Minst 1 symbol', valid: /[^a-zA-Z0-9]/.test(password) },
  ];

  return (
    <div className="auth-wrapper notranslate" translate="no">
      <div className="auth-container">
        <div className="auth-header">
          <h1 className="auth-title">Skapa Konto</h1>
          <p className="auth-subtitle">
            {step === 'details' ? (
              <span key="details-text">Börja bevaka lediga tider på Trafikverket</span>
            ) : (
              <span key="otp-text">
                Ange koden som skickades till <span className="email-display">{email}</span>
              </span>
            )}
          </p>
        </div>

        {step === 'details' ? (
          <form onSubmit={handleDetailsSubmit} className="auth-form">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label htmlFor="name">Namn</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ditt Namn"
                disabled={isLoading}
                autoComplete="name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">E-postadress</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="din.email@example.com"
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <label htmlFor="password">Lösenord</label>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    color: '#666',
                    cursor: 'pointer',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  title={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minst 12 tecken"
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <div
              className="password-requirements"
              style={{
                fontSize: '0.85rem',
                color: '#666',
                marginTop: '-10px',
                marginBottom: '15px',
                paddingLeft: '5px',
              }}
            >
              <p style={{ marginBottom: '5px', fontWeight: '500' }}>Lösenordet måste innehålla:</p>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '5px',
                }}
              >
                {passwordRequirements.map((req, index) => (
                  <li
                    key={index}
                    style={{
                      color: req.valid ? 'green' : '#666',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ marginRight: '6px', fontSize: '1.1em' }}>
                      {req.valid ? '✓' : '•'}
                    </span>
                    {req.label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Bekräfta Lösenord</label>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Upprepa lösenordet"
                disabled={isLoading}
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={isLoading}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Skickar kod...
                </>
              ) : (
                'Nästa: Verifiera E-post'
              )}
            </button>

            <div className="auth-links">
              <p>
                Har du redan ett konto? <Link to="/login">Logga in</Link>
              </p>
            </div>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} className="auth-form" translate="no">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group" style={{ textAlign: 'center' }}>
              <label style={{ marginBottom: '15px' }}>Verifieringskod (6 siffror)</label>

              <div className="otp-container notranslate">
                {otp.map((data, index) => (
                  <input
                    className="otp-input"
                    type="text"
                    name="otp"
                    maxLength="1"
                    key={index}
                    id={`otp-input-${index}`}
                    value={data}
                    onChange={(e) => handleChange(e.target, index)}
                    onKeyDown={(e) => handleBackspace(e, index)}
                    onPaste={handlePaste}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={isLoading || otp.join('').length < 6}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  Verifierar...
                </>
              ) : (
                'Verifiera & Skapa Konto'
              )}
            </button>

            <div className="auth-links">
              <button
                type="button"
                onClick={() => setStep('details')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Tillbaka till registrering
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Register;
